import { FC, useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { AsH1 } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { PhaseSlug, SweepstakeObject } from '@/types/codegen-federation'
import { useSafeAnalytics } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { GiveawayLogo } from '@/views/PreSales/PreSalesGiveawayView/components/Hero/GiveawayLogo'
import { GiveawayPlayButton } from '@/views/PreSales/PreSalesGiveawayView/components/Hero/GiveawayPlayButton'
import { GiveawayVideoModal } from '@/views/PreSales/PreSalesGiveawayView/components/Modals/GiveawayVideoModal'
import {
  GButtonMD,
  GEyebrowMD,
  GHeadingLG,
  GHeadingXS,
  GResponsiveText,
  GTitleLG,
  GTitleMD,
  GTitleMobile,
} from '@/views/PreSales/PreSalesGiveawayView/components/UI/Typography'
import { GiveawayPromoBanner } from './GiveawayPromoBanner'

interface Props {
  onScrollToProducts: () => void
  trailerId?: string | null | undefined
  title: string
  togglePauseBackgroundVideo: (value?: boolean | undefined) => void
  winnerAnnouncedDate?: SweepstakeObject['winnerAnnouncedDate']
  projectName: SweepstakeObject['projectName']
  theatricalSlug: string
  inTheatersText: string
  videoButtonText: string
  phase: PhaseSlug | null
  isActive: boolean
}
const GiveawayHero: FC<Props> = ({
  onScrollToProducts,
  title,
  trailerId,
  togglePauseBackgroundVideo,
  winnerAnnouncedDate,
  theatricalSlug,
  inTheatersText,
  phase,
  isActive,
  videoButtonText,
  projectName,
}) => {
  const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false)
  const { t } = useTranslate('tickets')
  const { push } = useSafeRouter()
  const { track } = useSafeAnalytics()

  const showInTheatersBadge = inTheatersText && phase !== 'guild_access'
  const showPresale = theatricalSlug === (slugs.david || slugs.truthAndTreason || slugs.iWasAStranger)

  const handleOpenTrailer = useCallback(() => {
    setIsTrailerModalOpen(true)
    togglePauseBackgroundVideo(true)
    track('Giveaway Video Clicked')
  }, [togglePauseBackgroundVideo, track])

  const handleCloseTrailer = useCallback(() => {
    setIsTrailerModalOpen(false)
    togglePauseBackgroundVideo(false)
  }, [togglePauseBackgroundVideo])

  const isSketch = theatricalSlug === slugs.sketch
  const isKok = theatricalSlug === slugs.kingOfKings
  const isTnT = theatricalSlug === slugs.truthAndTreason
  const isIwas = theatricalSlug === slugs.iWasAStranger

  const { heroTitle, btnText, onClick } = useMemo(() => {
    if (!isActive && phase === 'theatrical') {
      return {
        heroTitle: t('projectNowInTheaters', '{{projectName}} now in theaters', { projectName }),
        btnText: t('getShowtimes', 'Get Showtimes'),
        onClick: () => push(`/tickets/${theatricalSlug}`),
      }
    }

    if (!isActive && phase === 'guild_access') {
      return {
        heroTitle: t('watchProjectNow', 'Watch {{projectName}} Now', { projectName }),
        btnText: t('watchNow', 'Watch Now'),
        onClick: () => push(`/movies/${theatricalSlug}`),
      }
    }

    if ((isActive && phase === 'pre_sale') || showPresale || isIwas) {
      return {
        heroTitle: title,
        btnText: t('preOrderTicketsV2', 'Preorder Tickets'),
        onClick: onScrollToProducts,
      }
    }

    return { heroTitle: title, btnText: null, onClick: onScrollToProducts }
  }, [isActive, onScrollToProducts, phase, projectName, push, t, theatricalSlug, title, showPresale, isIwas])

  return (
    <div className="flex flex-1 flex-col">
      <div className="relative m-auto flex w-full max-w-[1080px] flex-1 flex-col gap-4 pt-4 md:flex-row md:items-center md:justify-center md:gap-10 md:pt-0">
        <FlexColumn className="gap-4 md:shrink-0">
          <GiveawayLogo />

          {showInTheatersBadge && (
            <InTheatersPill
              text={inTheatersText}
              className={classNames(
                'md:mt-4 flex mx-auto md:mx-0',
                isSketch ? 'rounded-none' : 'rounded-md',
                isIwas && '!bg-giveaway-primary-accent-dark',
              )}
            />
          )}

          {isTnT ? (
            <>
              <GResponsiveText
                className="text-center md:max-w-[480px] md:text-left"
                as={AsH1}
                color="white"
                MobileTextComponent={GTitleMD}
                DesktopTextComponent={GHeadingXS}
              >
                {t('WintheTripofaLifetime', 'Win the Trip of a Lifetime:')}
              </GResponsiveText>
              <GResponsiveText
                className="mx-auto -mt-2 max-w-[360px] text-center !normal-case  md:mx-0 md:max-w-[440px] md:text-left"
                as={AsH1}
                color="white"
                MobileTextComponent={GTitleLG}
                DesktopTextComponent={GHeadingLG}
              >
                {heroTitle}
              </GResponsiveText>
            </>
          ) : (
            <GResponsiveText
              className={classNames(
                'text-center uppercase md:max-w-[540px] md:text-left',
                slugs.iWasAStranger && '!font-bold',
              )}
              as={AsH1}
              color="white"
              MobileTextComponent={GTitleMobile}
              DesktopTextComponent={isKok ? GTitleMD : GHeadingLG}
            >
              {heroTitle}
            </GResponsiveText>
          )}

          <GiveawayPromoBanner
            isActive={isActive}
            theatricalSlug={theatricalSlug}
            winnerAnnouncedDate={winnerAnnouncedDate}
          />

          {btnText && (
            <div>
              <Button
                className={classNames(
                  'hidden flex-col  border-none bg-giveaway-primary-subtle !py-[17px] md:mr-auto md:mt-4 md:flex md:!px-7',
                  isSketch ? 'rounded-none' : 'rounded-[14px]',
                  isTnT && '!bg-giveaway-secondary',
                  isIwas && '!bg-giveaway-secondary-accent',
                )}
                onClick={onClick}
                variant="core-gray-900"
              >
                <GButtonMD color="white" className="flex items-center gap-2">
                  {btnText}
                </GButtonMD>
              </Button>
            </div>
          )}
        </FlexColumn>

        <div className="mx-auto h-full flex-1 items-center justify-center md:flex md:w-full">
          <GiveawayPlayButton
            text={videoButtonText ?? t('watchTrailer', 'Watch Trailer')}
            onClick={handleOpenTrailer}
          />
        </div>

        <FlexColumn className="gap-6 md:hidden">
          <Button
            className={classNames(
              'flex flex-col border-none bg-giveaway-primary-subtle !py-[17px] md:mr-auto md:!px-7',
              isSketch ? 'rounded-none' : 'rounded-[14px]',
              isIwas && '!bg-giveaway-secondary-accent',
            )}
            onClick={onClick}
            variant="core-gray-900"
          >
            <GButtonMD color="white" className="flex items-center gap-2">
              {btnText}
            </GButtonMD>
          </Button>
        </FlexColumn>
      </div>

      <GiveawayVideoModal
        handleClose={handleCloseTrailer}
        showModal={isTrailerModalOpen}
        theatricalSlug={theatricalSlug}
        youTubeUrl={`https://www.youtube-nocookie.com/embed/${trailerId}?autoplay=1`}
      />
    </div>
  )
}

export default GiveawayHero

const InTheatersPill = ({ className, text }: { className?: string; text: string }) => {
  return (
    <GEyebrowMD
      className={classNames(
        'w-fit items-center justify-center px-2 py-1 uppercase md:inline-flex bg-giveaway-primary-accent-light text-white',
        className,
      )}
    >
      {text}
    </GEyebrowMD>
  )
}
